/**
 * Whop Pixel on the hosted offer page (docs.whop.com/developer/ads/pixel),
 * scoped to the seller's account so the page counts as a Whop Ads
 * destination. Page views only: Whop records the sale itself for checkouts
 * it processes (the docs say never to fire `purchase` for those), and the
 * embedded checkout links its activity to the pixel visitor on its own.
 */

interface WhopPixel {
  setScope: (...accountIds: string[]) => void;
  track: (event: string, props?: Record<string, unknown>) => void;
}

declare global {
  interface Window {
    whop?: WhopPixel;
  }
}

const PIXEL_ORIGIN = 'https://t.whop.tw';
const ACCOUNT_ID = /^biz_[a-zA-Z0-9]+$/;
let trackedScope: string | null = null;

/** Load the pixel (once) and record a page view for the seller's account. */
export function trackOfferPageView(accountId: string): void {
  if (!ACCOUNT_ID.test(accountId) || trackedScope === accountId) return;
  trackedScope = accountId;

  // The documented snippet, unminified: queue calls until s.js loads.
  if (!window.whop) {
    const queue: unknown[][] = [];
    const stub = {
      q: queue,
      t: Date.now(),
      s: [] as string[],
      o: PIXEL_ORIGIN,
      track: (...args: unknown[]) => {
        queue.push([Date.now(), ...args]);
      },
      setScope: (...ids: string[]) => {
        stub.s = ids.filter((x) => typeof x === 'string');
        queue.push([Date.now(), 'setScope', ...stub.s]);
      },
      scope: (...ids: string[]) => ({
        track: (...args: unknown[]) => {
          queue.push([Date.now(), ...args, { __scope: ids }]);
        },
      }),
    };
    window.whop = stub;
    const script = document.createElement('script');
    script.async = true;
    script.src = `${PIXEL_ORIGIN}/s.js`;
    document.head.appendChild(script);
  }

  window.whop.setScope(accountId);
  window.whop.track('page');
}
